/**
 * Zoekgebied: welke buurten de Funda-zoektocht afloopt, en sinds MA-27 met
 * welke zoekfilters (oppervlakte, budget, energielabel).
 *
 * Links de kaart, rechts zoeken op naam en per wijk alles aan of uit; boven de
 * kaart de filters. Klikken en typen verandert eerst alleen een concept; de
 * teller rekent bij elke wijziging mee (dezelfde rekenkunde als de API, uit
 * @mamimap/shared; voor andere filters vraagt hij de API om de schatting).
 * Opslaan maakt er zoekopdrachten van: de postcodes van de gekozen buurten, in
 * zo weinig mogelijk zoekopdrachten die elk onder het paginaplafond blijven,
 * elk met de filters in de Funda-URL.
 *
 * Een lager budget vraagt eerst een bevestiging: een woning die boven het
 * budget uitstijgt valt uit de zoekopdracht en staat na de volgende volledige
 * ronde op verdwenen. Oppervlakte en label veranderen niet aan een woning, dus
 * die zijn veilig.
 */
import {
  type BekendTelling,
  energielabelKleur,
  FUNDA_DAGPLAFOND_PAGINAS,
  FUNDA_VENSTER_STANDAARD,
  formatAantal,
  formatEuro,
  heeftZoekfilters,
  LABELGROEPEN,
  type Labelgroep,
  maakZoekplan,
  vensterDoorlooptijdLabel,
  ZOEKFILTER_M2_MAX,
  ZOEKFILTER_PRIJS_MAX,
  ZOEKFILTERS_STANDAARD,
  ZOEKGEBIED_GRENS_MAX,
  ZOEKGEBIED_GRENS_MIN,
  type Zoekfilters,
  type ZoekgebiedUitslag,
  zelfdeZoekfilters,
} from '@mamimap/shared';
import { ChevronDown, ChevronRight, RotateCcw } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Bevestiging } from '@/components/Bevestiging';
import { Melding } from '@/components/Melding';
import {
  useBudgetGevolg,
  useBuurten,
  useFundaRust,
  useZetZoekgebied,
  useZetZoekgebiedGrens,
  useZoekgebied,
  useZoekgebiedSchatting,
} from '@/lib/api/queries';
import { ZoekgebiedKaart } from './ZoekgebiedKaart';
import {
  budgetVerlaagd,
  leesFilterGetal,
  telAan,
  type Wijk,
  wijkenUit,
  wisselBuurt,
  wisselWijk,
  zelfdeKeuze,
  zoekInWijken,
} from './zoekgebied-keuze';

export function Zoekgebied() {
  const buurten = useBuurten();
  const stand = useZoekgebied();
  const opslaan = useZetZoekgebied();
  const grens = useZetZoekgebiedGrens();
  /* Voor de doorlooptijd in de teller (MA-32); dezelfde query als de regel erboven. */
  const rust = useFundaRust();

  const [open, zetOpen] = useState(true);
  const [concept, zetConcept] = useState<Set<string> | null>(null);
  const [filterConcept, zetFilterConcept] = useState<Zoekfilters | null>(null);
  const [zoek, zetZoek] = useState('');
  const [openWijken, zetOpenWijken] = useState<ReadonlySet<string>>(new Set());
  const [uitslag, zetUitslag] = useState<ZoekgebiedUitslag | null>(null);
  /* De filters waarvoor een lager budget nog bevestigd moet worden. */
  const [bevestigBudget, zetBevestigBudget] = useState<Zoekfilters | null>(null);

  const bewaard = stand.data?.aan ?? [];
  const bewaardeFilters = stand.data?.filters ?? ZOEKFILTERS_STANDAARD;
  const gekozen = useMemo(() => concept ?? new Set(bewaard), [concept, bewaard]);
  const filters = filterConcept ?? bewaardeFilters;
  const filtersGewijzigd = !zelfdeZoekfilters(filters, bewaardeFilters);
  const gewijzigd =
    (concept !== null && !zelfdeKeuze(concept, bewaard)) ||
    filtersGewijzigd ||
    (stand.data ? !stand.data.urlsActueel : false);

  /* Voor andere filters dan de opgeslagen rekent de API de schatting opnieuw;
     tot die er is blijft de vorige staan (keepPreviousData). */
  const andereSchatting = useZoekgebiedSchatting(filters, filtersGewijzigd);
  const schattingBron = filtersGewijzigd ? andereSchatting.data : stand.data;

  const wijken = useMemo(() => wijkenUit(buurten.data), [buurten.data]);
  const gevonden = useMemo(() => zoekInWijken(wijken, zoek), [wijken, zoek]);

  const postcodesPerBuurt = useMemo(
    () =>
      new Map(
        (buurten.data?.features ?? []).map((f) => [f.properties.buurtcode, f.properties.postcodes]),
      ),
    [buurten.data],
  );
  const schatting = useMemo(
    () => new Map(Object.entries(schattingBron?.schatting ?? {})),
    [schattingBron?.schatting],
  );
  const schattingZonderFilters = useMemo(
    () => new Map(Object.entries(schattingBron?.schattingZonderFilters ?? {})),
    [schattingBron?.schattingZonderFilters],
  );
  const plan = useMemo(
    () => maakZoekplan([...gekozen], postcodesPerBuurt, schatting, stand.data?.paginasPerOpdracht),
    [gekozen, postcodesPerBuurt, schatting, stand.data?.paginasPerOpdracht],
  );
  const planZonderFilters = useMemo(
    () =>
      heeftZoekfilters(filters)
        ? maakZoekplan(
            [...gekozen],
            postcodesPerBuurt,
            schattingZonderFilters,
            stand.data?.paginasPerOpdracht,
          )
        : null,
    [filters, gekozen, postcodesPerBuurt, schattingZonderFilters, stand.data?.paginasPerOpdracht],
  );
  const bekend = useMemo(
    () => telBekend(plan.postcodes, schattingBron?.bekend ?? {}),
    [plan.postcodes, schattingBron?.bekend],
  );
  const meegezocht = useMemo(() => {
    const inPlan = new Set(plan.postcodes);
    const uit = new Set<string>();
    for (const [code, postcodes] of postcodesPerBuurt) {
      if (postcodes.length > 0 && postcodes.every((pc) => inPlan.has(pc))) uit.add(code);
    }
    return uit;
  }, [plan.postcodes, postcodesPerBuurt]);

  const grensPaginas = stand.data?.grensPaginas ?? 100;
  const teVeel = plan.paginas > grensPaginas;
  /* Sinds MA-46 loopt de vanger op vaste tijden: de doorlooptijd volgt uit
     hoeveel keer per dag en hoeveel pagina's per keer, niet meer uit de wekker. */
  const doorlooptijd = vensterDoorlooptijdLabel({
    paginas: plan.paginas,
    tijden: rust.data?.venster.tijden.length ?? FUNDA_VENSTER_STANDAARD.tijden.length,
    perKeer: rust.data?.venster.paginas ?? FUNDA_VENSTER_STANDAARD.paginas,
    dagplafond: rust.data?.dagplafond ?? FUNDA_DAGPLAFOND_PAGINAS,
  });

  function wijzig(nieuw: Set<string>) {
    zetConcept(nieuw);
    zetUitslag(null);
    zetBevestigBudget(null);
  }

  function wijzigFilters(nieuw: Zoekfilters) {
    zetFilterConcept(nieuw);
    zetUitslag(null);
    zetBevestigBudget(null);
  }

  function terug() {
    zetConcept(new Set(bewaard));
    zetFilterConcept(null);
    zetUitslag(null);
    zetBevestigBudget(null);
  }

  function bewaar() {
    opslaan.mutate(
      { buurtcodes: [...gekozen], filters },
      {
        onSuccess: (antwoord) => {
          zetConcept(null);
          zetFilterConcept(null);
          zetBevestigBudget(null);
          zetUitslag(antwoord);
        },
      },
    );
  }

  function vraagOfBewaar() {
    if (budgetVerlaagd(bewaardeFilters.maxVraagprijs, filters.maxVraagprijs)) {
      zetBevestigBudget(filters);
    } else {
      bewaar();
    }
  }

  function wisselOpenWijk(code: string) {
    const nieuw = new Set(openWijken);
    if (nieuw.has(code)) nieuw.delete(code);
    else nieuw.add(code);
    zetOpenWijken(nieuw);
  }

  const zoekt = zoek.trim() !== '';

  return (
    <section className="border border-line bg-surface-strong">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 px-3 py-2">
        <button
          type="button"
          className="flex items-center gap-1 text-[13px] font-medium text-ink"
          aria-expanded={open}
          onClick={() => zetOpen(!open)}
        >
          {open ? (
            <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />
          ) : (
            <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />
          )}
          Zoekgebied
        </button>

        <p className="cijfers text-[12.5px] text-ink-soft" aria-live="polite">
          {formatAantal(gekozen.size)} {gekozen.size === 1 ? 'buurt' : 'buurten'} ·{' '}
          {formatAantal(plan.postcodes.length)} postcodes · ≈ {formatAantal(plan.woningen)} woningen
          · ≈ {formatAantal(plan.paginas)} {plan.paginas === 1 ? 'pagina' : "pagina's"} per ronde ·{' '}
          {formatAantal(plan.groepen.length)}{' '}
          {plan.groepen.length === 1 ? 'zoekopdracht' : 'zoekopdrachten'}
          {doorlooptijd ? ` · ${doorlooptijd}` : null}
          {planZonderFilters ? (
            <span className="text-muted">
              {' '}
              · zonder filters ≈ {formatAantal(planZonderFilters.woningen)} woningen,{' '}
              {formatAantal(planZonderFilters.paginas)}{' '}
              {planZonderFilters.paginas === 1 ? 'pagina' : "pagina's"}
            </span>
          ) : null}
        </p>

        <div className="ml-auto flex items-center gap-2">
          {gewijzigd ? (
            <button type="button" className="knop-stil" onClick={terug}>
              <RotateCcw className="h-3.5 w-3.5" aria-hidden="true" />
              Terug
            </button>
          ) : null}
          <button
            type="button"
            className="knop"
            disabled={!gewijzigd || opslaan.isPending || bevestigBudget !== null}
            onClick={vraagOfBewaar}
          >
            {opslaan.isPending ? 'Opslaan ...' : 'Opslaan'}
          </button>
        </div>
      </div>

      {teVeel ? (
        <p className="border-t border-line px-3 py-1.5 text-[12.5px] text-brick">
          Boven de grens van {formatAantal(grensPaginas)} pagina's per ronde. Zoveel verzoeken
          brengen Funda sneller tot een blokkade.
        </p>
      ) : null}

      {stand.data && !stand.data.urlsActueel && !uitslag ? (
        <p className="border-t border-line px-3 py-1.5 text-[12.5px] text-ink-soft">
          De zoekopdrachten van het zoekgebied zoeken nog niet met deze filters; opslaan zet hun URL
          gelijk.
        </p>
      ) : null}

      {uitslag ? (
        <p className="border-t border-line px-3 py-1.5 text-[12.5px] text-ink-soft">
          Opgeslagen. {samenvatUitslag(uitslag)}
        </p>
      ) : null}

      {bevestigBudget !== null ? (
        <div className="border-t border-line p-2">
          <BudgetBevestiging
            filters={bevestigBudget}
            bezig={opslaan.isPending}
            fout={opslaan.error}
            onBevestig={bewaar}
            onAnnuleer={() => zetBevestigBudget(null)}
          />
        </div>
      ) : null}

      {opslaan.error && bevestigBudget === null ? <Melding fout={opslaan.error} /> : null}
      {buurten.error ? <Melding fout={buurten.error} /> : null}
      {stand.error ? <Melding fout={stand.error} /> : null}
      {andereSchatting.error ? <Melding fout={andereSchatting.error} /> : null}

      {open ? (
        <ZoekfilterVelden
          key={filterSleutel(bewaardeFilters)}
          filters={filters}
          bekend={bekend}
          onWijzig={wijzigFilters}
        />
      ) : null}

      {open && buurten.data && buurten.data.features.length === 0 ? (
        <p className="border-t border-line px-3 py-3 text-[12px] text-muted">
          Er staan nog geen buurten in de database. Haal ze op met{' '}
          <span className="cijfers">pnpm db:haal-buurten</span>.
        </p>
      ) : null}

      {open && buurten.data && buurten.data.features.length > 0 ? (
        <div className="grid gap-2 border-t border-line p-2 lg:grid-cols-[minmax(0,1fr)_300px]">
          <ZoekgebiedKaart
            kaart={buurten.data}
            gekozen={gekozen}
            meegezocht={meegezocht}
            onWissel={(code) => wijzig(wisselBuurt(gekozen, code))}
          />

          <div className="flex min-h-0 flex-col gap-1.5 lg:h-[460px]">
            <input
              className="veld"
              type="search"
              aria-label="Buurt zoeken"
              placeholder="Zoek een buurt, wijk of postcode"
              value={zoek}
              onChange={(event) => zetZoek(event.target.value)}
            />

            <div className="min-h-0 flex-1 overflow-y-auto border border-line">
              {gevonden.map((wijk) => (
                <WijkRegel
                  key={wijk.wijkcode}
                  wijk={wijk}
                  gekozen={gekozen}
                  open={zoekt || openWijken.has(wijk.wijkcode)}
                  onOpen={() => wisselOpenWijk(wijk.wijkcode)}
                  onWisselWijk={() => wijzig(wisselWijk(gekozen, wijk))}
                  onWisselBuurt={(code) => wijzig(wisselBuurt(gekozen, code))}
                />
              ))}
              {gevonden.length === 0 ? (
                <p className="px-2 py-2 text-[12px] text-muted">Geen buurt met deze naam.</p>
              ) : null}
            </div>

            {plan.groepen.length > 0 ? (
              <div className="border border-line px-2 py-1.5 text-[12px]">
                <div className="mb-0.5 text-[11.5px] text-muted">Zoekopdrachten na opslaan</div>
                {plan.groepen.map((groep) => (
                  <div key={groep.postcodes.join()} className="flex gap-2">
                    <span className="cijfers min-w-0 flex-1 truncate text-ink-soft">
                      {groep.postcodes.join(', ')}
                    </span>
                    <span className="cijfers shrink-0 text-right text-muted">
                      ≈ {formatAantal(groep.paginas)} p.
                    </span>
                  </div>
                ))}
              </div>
            ) : null}

            <label className="flex items-center gap-2 text-[12px] text-muted">
              Waarschuwen boven
              <input
                key={grensPaginas}
                className="veld cijfers w-16 text-right"
                type="number"
                min={ZOEKGEBIED_GRENS_MIN}
                max={ZOEKGEBIED_GRENS_MAX}
                defaultValue={grensPaginas}
                onBlur={(event) => {
                  const waarde = Number(event.target.value);
                  if (
                    Number.isInteger(waarde) &&
                    waarde >= ZOEKGEBIED_GRENS_MIN &&
                    waarde <= ZOEKGEBIED_GRENS_MAX &&
                    waarde !== grensPaginas
                  ) {
                    grens.mutate(waarde);
                  }
                }}
              />
              pagina's per ronde
            </label>
          </div>
        </div>
      ) : null}
    </section>
  );
}

/* --- de zoekfilters -------------------------------------------------------- */

type ZoekfilterVeldenProps = {
  filters: Zoekfilters;
  /** Bekende actieve woningen in de gekozen postcodes, en hoeveel voldoen. */
  bekend: BekendTelling;
  onWijzig: (filters: Zoekfilters) => void;
};

/**
 * De velden voor oppervlakte en budget, en de labelknoppen. De getallen gaan
 * pas bij verlaten van het veld (of Enter) naar het concept, zodat niet elke
 * toetsaanslag een schatting bij de API vraagt. De labels meteen.
 */
function ZoekfilterVelden({ filters, bekend, onWijzig }: ZoekfilterVeldenProps) {
  const [m2Tekst, zetM2Tekst] = useState(
    filters.minOppervlakteM2 == null ? '' : String(filters.minOppervlakteM2),
  );
  const [budgetTekst, zetBudgetTekst] = useState(
    filters.maxVraagprijs == null ? '' : String(filters.maxVraagprijs),
  );

  function legM2Vast() {
    const waarde = leesFilterGetal(m2Tekst, ZOEKFILTER_M2_MAX);
    zetM2Tekst(waarde == null ? '' : String(waarde));
    if (waarde !== filters.minOppervlakteM2) onWijzig({ ...filters, minOppervlakteM2: waarde });
  }

  function legBudgetVast() {
    const waarde = leesFilterGetal(budgetTekst, ZOEKFILTER_PRIJS_MAX);
    zetBudgetTekst(waarde == null ? '' : String(waarde));
    if (waarde !== filters.maxVraagprijs) onWijzig({ ...filters, maxVraagprijs: waarde });
  }

  function wisselLabel(groep: Labelgroep) {
    const set = new Set(filters.energielabels);
    if (set.has(groep)) set.delete(groep);
    else set.add(groep);
    onWijzig({ ...filters, energielabels: LABELGROEPEN.filter((een) => set.has(een)) });
  }

  const bijEnter = (leg: () => void) => (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      leg();
    }
  };

  return (
    <div className="border-t border-line px-3 py-2">
      <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-[12px]">
        <span className="text-[12.5px] font-medium text-ink">Zoekfilters</span>

        <label className="flex items-center gap-1.5 text-muted">
          Minimaal
          <input
            className="veld cijfers w-16 text-right"
            inputMode="numeric"
            aria-label="Minimale woonoppervlakte in vierkante meter"
            value={m2Tekst}
            onChange={(event) => zetM2Tekst(event.target.value)}
            onBlur={legM2Vast}
            onKeyDown={bijEnter(legM2Vast)}
          />
          m²
        </label>

        <label className="flex items-center gap-1.5 text-muted">
          Budget tot €
          <input
            className="veld cijfers w-24 text-right"
            inputMode="numeric"
            aria-label="Maximale vraagprijs in euro"
            value={budgetTekst}
            onChange={(event) => zetBudgetTekst(event.target.value)}
            onBlur={legBudgetVast}
            onKeyDown={bijEnter(legBudgetVast)}
          />
        </label>

        <fieldset className="flex items-center gap-1.5 text-muted">
          <legend className="sr-only">Energielabels</legend>
          Label
          <div className="flex gap-1">
            {LABELGROEPEN.map((groep) => {
              const aan = filters.energielabels.includes(groep);
              return (
                <button
                  type="button"
                  key={groep}
                  aria-pressed={aan}
                  aria-label={`Label ${groep}${groep === 'A' ? ' (ook A+ en hoger)' : ''}`}
                  onClick={() => wisselLabel(groep)}
                  className={`flex h-7 w-7 items-center justify-center gap-1 border text-[11.5px] ${
                    aan
                      ? 'border-accent bg-accent text-white'
                      : 'border-line-strong text-ink-soft hover:bg-paper'
                  }`}
                >
                  <span
                    className="h-2 w-2 shrink-0"
                    style={{ backgroundColor: energielabelKleur(groep) }}
                    aria-hidden="true"
                  />
                  {groep}
                </button>
              );
            })}
          </div>
        </fieldset>

        <span className="cijfers text-muted">
          {formatAantal(bekend.voldoet)} van {formatAantal(bekend.totaal)} bekende woningen in het
          zoekgebied voldoen
        </span>
      </div>

      <p className="mt-1.5 text-[11.5px] text-muted">
        Een woning die boven het budget uitstijgt valt uit de zoekopdracht en staat na de volgende
        volledige ronde als verdwenen (uit het aanbod, niet verkocht). Oppervlakte en label
        veranderen niet aan een woning; die filters zijn veilig.
      </p>
    </div>
  );
}

type BudgetBevestigingProps = {
  /** De filters die opgeslagen gaan worden; het budget erin is lager dan het opgeslagen. */
  filters: Zoekfilters;
  bezig: boolean;
  fout: unknown;
  onBevestig: () => void;
  onAnnuleer: () => void;
};

/** De bevestiging bij een lager budget, met het aantal dat eruit valt. */
function BudgetBevestiging({
  filters,
  bezig,
  fout,
  onBevestig,
  onAnnuleer,
}: BudgetBevestigingProps) {
  const gevolg = useBudgetGevolg(filters);
  const maxVraagprijs = filters.maxVraagprijs ?? 0;
  const aantal = gevolg.data?.vallenUit;
  const vraag =
    aantal == null
      ? `Budget verlagen naar ${formatEuro(maxVraagprijs)}?`
      : `Budget verlagen naar ${formatEuro(maxVraagprijs)}? ${formatAantal(aantal)} bekende actieve ${
          aantal === 1 ? 'woning' : 'woningen'
        } in het zoekgebied ${aantal === 1 ? 'valt' : 'vallen'} daardoor uit de zoekopdrachten.`;
  return (
    <Bevestiging
      vraag={vraag}
      uitleg={
        gevolg.isPending
          ? 'Aan het tellen hoeveel woningen erboven liggen.'
          : 'Die staan na de volgende volledige ronde op verdwenen (uit het aanbod); hun historie blijft bewaard.'
      }
      knop="Verlagen en opslaan"
      bezig={bezig || gevolg.isPending}
      fout={fout ?? gevolg.error}
      onBevestig={onBevestig}
      onAnnuleer={onAnnuleer}
    />
  );
}

/* --- hulpjes --------------------------------------------------------------- */

function telBekend(
  postcodes: readonly string[],
  bekend: Record<string, BekendTelling>,
): BekendTelling {
  let totaal = 0;
  let voldoet = 0;
  for (const pc of postcodes) {
    const telling = bekend[pc];
    if (!telling) continue;
    totaal += telling.totaal;
    voldoet += telling.voldoet;
  }
  return { totaal, voldoet };
}

/** Verandert als de opgeslagen filters veranderen; dan beginnen de velden opnieuw. */
function filterSleutel(filters: Zoekfilters): string {
  return `${filters.minOppervlakteM2 ?? ''}|${filters.maxVraagprijs ?? ''}|${filters.energielabels.join('')}`;
}

type WijkRegelProps = {
  wijk: Wijk;
  gekozen: ReadonlySet<string>;
  open: boolean;
  onOpen: () => void;
  onWisselWijk: () => void;
  onWisselBuurt: (buurtcode: string) => void;
};

function WijkRegel({ wijk, gekozen, open, onOpen, onWisselWijk, onWisselBuurt }: WijkRegelProps) {
  const aan = telAan(wijk, gekozen);
  const alles = aan === wijk.buurten.length;
  return (
    <div className="border-b border-line last:border-b-0">
      <div className="flex items-center gap-1 px-1 py-[2px] text-[12px]">
        <button
          type="button"
          className="flex min-w-0 flex-1 items-center gap-1 text-left text-ink"
          aria-expanded={open}
          onClick={onOpen}
        >
          {open ? (
            <ChevronDown className="h-3 w-3 shrink-0 text-faint" aria-hidden="true" />
          ) : (
            <ChevronRight className="h-3 w-3 shrink-0 text-faint" aria-hidden="true" />
          )}
          <span className="truncate">{wijk.wijknaam}</span>
        </button>
        <span className="cijfers shrink-0 text-[11.5px] text-muted">
          {aan}/{wijk.buurten.length}
        </span>
        <button
          type="button"
          className={`shrink-0 border px-1.5 text-[11px] ${
            alles
              ? 'border-accent bg-accent text-white'
              : 'border-line-strong text-ink-soft hover:bg-paper'
          }`}
          aria-pressed={alles}
          aria-label={`${wijk.wijknaam}: ${alles ? 'alles uit' : 'alles aan'}`}
          onClick={onWisselWijk}
        >
          {alles ? 'alles uit' : 'alles aan'}
        </button>
      </div>
      {open
        ? wijk.buurten.map((buurt) => {
            const isAan = gekozen.has(buurt.buurtcode);
            return (
              <button
                type="button"
                key={buurt.buurtcode}
                aria-pressed={isAan}
                onClick={() => onWisselBuurt(buurt.buurtcode)}
                className={`flex w-full items-center gap-2 py-[2px] pr-2 pl-5 text-left text-[12px] ${
                  isAan ? 'bg-accent-soft text-accent-deep' : 'text-ink-soft hover:bg-paper'
                }`}
              >
                <span className="truncate">{buurt.buurtnaam}</span>
                <span className="cijfers ml-auto shrink-0 text-[11px] text-faint">
                  {buurt.postcodes.join(', ')}
                </span>
              </button>
            );
          })
        : null}
    </div>
  );
}

function samenvatUitslag(uitslag: ZoekgebiedUitslag): string {
  const delen: string[] = [];
  if (uitslag.nieuw.length) delen.push(`${uitslag.nieuw.length} nieuw`);
  if (uitslag.bijgewerkt.length) delen.push(`${uitslag.bijgewerkt.length} bijgewerkt`);
  if (uitslag.aangezet.length) delen.push(`${uitslag.aangezet.length} weer aan`);
  if (uitslag.uitgezet.length) delen.push(`${uitslag.uitgezet.length} uitgezet`);
  if (uitslag.ongewijzigd.length) delen.push(`${uitslag.ongewijzigd.length} ongewijzigd`);
  return delen.length > 0 ? `Zoekopdrachten: ${delen.join(', ')}.` : 'Geen zoekopdrachten.';
}
